import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { SchoolYearValue, useResultsDataContext } from '../../ResultsDataContext';
import { useFilterInLocalStorage } from '../../../components/per-student/hooks/useFilterInLocalStorage';
import { FilterSearchParam } from '../../../../../../shared-components/elementis/types/FilterSearchParam';

interface Opts {
  selectedTab: 'STUDENT' | 'EXERCISE';
}

export function useSelectedSchoolYear({ selectedTab }: Opts) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { schoolYears, groups, modules } = useResultsDataContext();
  const { getStoredData } = useFilterInLocalStorage(modules[0]?.id);

  const selectedSchoolYearId = searchParams.get(FilterSearchParam.SchoolYear);

  const selectedSchoolYear = useMemo(() => {
    if (selectedSchoolYearId) {
      return Object.values(schoolYears).find(x => x.id === selectedSchoolYearId) || null;
    }

    return null;
  }, [selectedSchoolYearId, schoolYears]);

  const setSelectedSchoolYear = useCallback(
    (schoolYear: SchoolYearValue) => {
      if (!groups.length) return;

      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        newParams.set(FilterSearchParam.SchoolYear, schoolYear.id);

        const groupsToSelect =
          selectedTab === 'STUDENT'
            ? [groups.find(x => x.schoolYear === schoolYear.id)!]
            : groups.filter(x => x.schoolYear === schoolYear.id);

        newParams.set(FilterSearchParam.Groups, groupsToSelect.map(g => g.id).join(','));
        newParams.set(FilterSearchParam.School, groupsToSelect[0].school.id);

        return newParams;
      });
    },
    [groups, setSearchParams, selectedTab],
  );

  useEffect(() => {
    if (selectedSchoolYearId) return;

    const storedData = getStoredData();
    if (storedData === null) return;

    const schoolYear = Object.values(schoolYears).find(sy => sy.id === storedData.schoolYearId);
    if (!schoolYear) return;

    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set(FilterSearchParam.SchoolYear, schoolYear.id);
      return newParams;
    });
  }, [getStoredData, schoolYears, selectedSchoolYearId, setSearchParams]);

  return [selectedSchoolYear, setSelectedSchoolYear] as const;
}
